import React, { useEffect, useState } from 'react';
import { Alert, MenuItem, Stack, TextField, Typography } from '@mui/material';
import * as ETVConstants from '../../ETVConstants';

export const directRouting = () => ({ call_mode: 'direct', motor_room_user_id: '', motor_room_address: '', pbx_destination: '' });
export function routingError(value, cloud) {
  if (!value || value.call_mode === 'direct') return '';
  if (cloud && !value.motor_room_user_id) return 'Select an additional call endpoint.';
  if (!cloud && (!value.motor_room_address?.trim() || !value.pbx_destination?.trim()))
    return 'Enter the additional endpoint address and the PBX ring-group or conference destination.';
  return '';
}

export default function IntercomCallRoutingFields({ value, onChange, cloud, screenID = 0 }) {
  const routing = value || directRouting();
  const [phones, setPhones] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!cloud) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`${ETVConstants.getServerBase()}/intercom_admin?action=motor_room_candidates&screen_user_id=${screenID}`, {
      headers: { user_id: localStorage.getItem('etv.user_id'), access_token: localStorage.getItem('etv.access_token') },
      signal: controller.signal,
    }).then(r => r.json()).then(r => {
      if (!r.result) throw new Error(r.reason || 'Could not load available SIP phones.');
      setPhones(r.list || []);
    }).catch(e => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [cloud, screenID]);
  const update = (key, val) => onChange({ ...routing, [key]: val });
  return <Stack spacing={2}>
    {routing.provisioning_pending && <Alert severity="info">PBX configuration is pending. Alarm calls continue directly to the scheduled FCC contact until the cloud PBXs have accepted the routing.</Alert>}
    <TextField select fullWidth label="Emergency call mode" value={routing.call_mode}
      onChange={e => onChange({ ...directRouting(), call_mode: e.target.value })}>
      <MenuItem value="direct">Direct call (1:1)</MenuItem>
      <MenuItem value="ring_group">Ring group (first answer)</MenuItem>
      <MenuItem value="conference">Three-way conference</MenuItem>
    </TextField>
    {routing.call_mode !== 'direct' && <>
      <Typography variant="body2" color="text.secondary">
        {routing.call_mode === 'ring_group'
          ? 'FCC and the additional endpoint ring together. The first answer connects; the other phone stops ringing.'
          : 'FCC and the additional endpoint ring together and can both join. The call ends when the last remote participant leaves.'}
      </Typography>
      {cloud ? <>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select required fullWidth label="Additional call endpoint" disabled={loading}
          value={routing.motor_room_user_id || ''} onChange={e => update('motor_room_user_id', e.target.value)}
          helperText={loading ? 'Loading available phones...' : 'Select a SIP desk phone or mobile SIP app, with or without video. The same endpoint may serve multiple lifts. Create a third-party Cloud SIP device first if needed.'}>
          <MenuItem value="">Select an available SIP phone</MenuItem>
          {phones.map(p => <MenuItem key={p.intercom_user_id} value={p.intercom_user_id}>
            {p.friendly_name || 'SIP phone'} — {p.sip_extension}@{p.sip_domain}
          </MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">FCC selection uses the existing contact list and availability schedule. FCC contacts must be Cloud SIP devices on the same PBX cluster. The additional endpoint must not also appear in this lift's FCC contact list.</Typography>
      </> : <>
        <TextField required fullWidth label="Additional endpoint SIP address" value={routing.motor_room_address || ''}
          onChange={e => update('motor_room_address', e.target.value)} helperText="Extension or full sip:extension@domain address." />
        <TextField required fullWidth label="PBX ring-group / conference destination" value={routing.pbx_destination || ''}
          onChange={e => update('pbx_destination', e.target.value)} helperText="The number the car calls. Configure members, FCC routing and hangup behaviour on the on-premise PBX." />
        <Alert severity="info">Configure and validate this route on the Grandstream PBX, including the backup PBX. These settings do not provision an external PBX.</Alert>
      </>}
    </>}
  </Stack>;
}
